import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../../hooks/useAuthContext';
import { axiosInstance } from '../../context/AuthContext';
import SavedArtworksSection from '../artozphere/SavedArtworksSection';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const UserProfileTab = () => {
  const { user } = useAuthContext();
  const [title, setTitle] = useState('');
  const [bio, setBio] = useState('');
  const [avatar, setAvatar] = useState('');
  const [message, setMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const avatarInputRef = useRef(null);

  const storageKey = useMemo(
    () => `bio-draft-${user?.id || user?._id || 'me'}`,
    [user]
  );

  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setTitle(parsed.title || '');
        setBio(parsed.bio || '');
      } catch (e) {
        console.warn('Failed to parse stored profile draft', e);
      }
    }
  }, [storageKey]);

  useEffect(() => {
    const fetchBio = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await axiosInstance.get('/bio/me');
        if (response.data?.bio) {
          const b = response.data.bio;
          setTitle(b.title || '');
          setBio(b.bio || '');
          setAvatar(b.avatar || '');
        }
      } catch (error) {
        console.error('Failed to load profile', error);
        setMessage('Unable to load your saved profile right now.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBio();
  }, [user]);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage('Please choose an image file.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setMessage('Image is too large. Please choose a file under 5 MB.');
      e.target.value = '';
      return;
    }

    try {
      const base64 = await fileToBase64(file);
      setAvatar(base64);
      setMessage('');
    } catch (err) {
      console.error('Failed to read image', err);
      setMessage('Failed to read image. Please try again.');
    } finally {
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!user) {
      setMessage('Please log in to save your profile.');
      return;
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify({ title, bio }));
    } catch (storageErr) {
      console.warn('Could not cache draft locally', storageErr);
    }

    try {
      setIsSaving(true);
      await axiosInstance.post('/bio', { title, bio, avatar, status: 1 });
      setMessage('Profile saved.');
    } catch (error) {
      console.error('Failed to save profile', error);
      const serverMsg =
        error?.response?.data?.error ||
        error?.response?.statusText ||
        error?.message ||
        'Unknown error';
      const status413 = error?.response?.status === 413;
      setMessage(
        status413
          ? 'Image is too large for the server. Try a smaller photo.'
          : `Failed to save profile: ${serverMsg}`
      );
    } finally {
      setIsSaving(false);
    }
  };

  const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Artist'
  )}&background=random&size=240`;

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Manage your artist or curator profile for Artization analytics.
      </p>

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Profile photo</h2>
        <div className="flex items-center gap-4">
          <img
            src={avatar || fallbackAvatar}
            alt="Profile preview"
            className="w-24 h-24 rounded-full object-cover border border-gray-200 bg-gray-100"
          />
          <div className="flex flex-col gap-2">
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={isLoading}
              className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-800 border border-gray-300 hover:bg-gray-200 transition disabled:opacity-60"
            >
              {avatar ? 'Change photo' : 'Upload photo'}
            </button>
            {avatar && (
              <button
                type="button"
                onClick={() => setAvatar('')}
                className="text-sm text-red-600 hover:underline self-start"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">About</h2>
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="profile-title">
            Title
          </label>
          <input
            id="profile-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g. Contemporary Artist, Gallery Curator"
            disabled={isLoading}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="profile-bio">
            Bio
          </label>
          <textarea
            id="profile-bio"
            rows={6}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="A short professional bio for your analytics dashboard."
            disabled={isLoading}
          />
        </div>
      </section>

      <div className="flex flex-wrap gap-3 items-center">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || isLoading}
          className="rounded-lg bg-blue-600 px-4 py-2 text-white text-sm font-medium hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save profile'}
        </button>
        {message && (
          <span className={`text-sm ${message.includes('Failed') ? 'text-red-600' : 'text-green-700'}`}>
            {message}
          </span>
        )}
      </div>

      <SavedArtworksSection />
    </div>
  );
};

export default UserProfileTab;
